/**
 * The create-tenant wizard (design-reference §4.14c): a three-step modal —
 * Tenant (name + plan), Admin (name + email, no password) and Defaults
 * (confidence threshold + branding hostname) — that provisions a real tenant
 * via `POST /v1/tenants`.
 *
 * The admin's email and display name are sent as part of that request and
 * provisioned into Auth0 with an emailed set-password ticket; the branding
 * hostname is collected for the operator's intent but is not part of the
 * request, so it is not sent — re-skinning is a separate flow the backend owns.
 */
import { useId, useState } from 'react';

import { Button, FormField, Input, Modal, useToast } from '../../components';
import { ApiError } from '../../api/errors';
import type { TenantView } from '../../api/schema';

import type { AdminApi } from './api';
import { hostnameFor } from './format';

type Plan = 'trial' | 'standard' | 'plus';
const PLANS: readonly { value: Plan; label: string }[] = [
  { value: 'trial', label: 'Trial' },
  { value: 'standard', label: 'Standard' },
  { value: 'plus', label: 'Plus' },
];

const STEPS = ['Tenant', 'Admin', 'Defaults'] as const;

export interface CreateTenantWizardProps {
  open: boolean;
  onClose: () => void;
  /** The partner the new tenant is created under. */
  partnerId: string;
  /** The typed admin API (injected so the screen owns one instance). */
  api: AdminApi;
  /** Called with the created tenant after a successful provision. */
  onCreated: (tenant: TenantView) => void;
}

export function CreateTenantWizard({
  open,
  onClose,
  partnerId,
  api,
  onCreated,
}: CreateTenantWizardProps): React.JSX.Element | null {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<Plan>('standard');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [threshold, setThreshold] = useState(0.75);
  const [hostname, setHostname] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which field the server's refusal belongs to, so it shows where it is fixed. */
  const [errorField, setErrorField] = useState<'email' | null>(null);

  const nameId = useId();
  const planId = useId();
  const thresholdId = useId();
  const hostnameId = useId();

  if (!open) return null;

  const trimmedName = name.trim();
  const canAdvanceStep1 = trimmedName.length > 0;
  const canAdvanceStep2 = adminName.trim().length > 0 && email.trim().length > 0;
  const isLast = step === STEPS.length - 1;

  const reset = (): void => {
    setStep(0);
    setName('');
    setPlan('standard');
    setAdminName('');
    setEmail('');
    setThreshold(0.75);
    setHostname('');
    setError(null);
    setErrorField(null);
    setSubmitting(false);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const back = (): void => {
    if (step === 0) {
      close();
      return;
    }
    setStep((s) => s - 1);
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    setErrorField(null);
    try {
      const tenant = await api.tenants.create({
        partner_id: partnerId,
        name: trimmedName,
        plan,
        settings: { confidence_threshold: threshold },
        admin: {
          email: email.trim(),
          display_name: adminName.trim(),
        },
      });
      toast('Tenant created — an email to set a password has been sent to the admin');
      onCreated(tenant);
      close();
    } catch (err) {
      // The refusal is about the account, which is step 2 — so go back to it
      // rather than leaving the operator on Defaults with an error about a
      // field they cannot see. Nothing was created; the whole provision is
      // all-or-nothing, so re-submitting after a fix is safe.
      if (err instanceof ApiError && err.status === 409) {
        setError('That email is already in use');
        setErrorField('email');
        setStep(1);
      } else {
        setError('Could not create the tenant. Check the details and try again.');
      }
      setSubmitting(false);
    }
  };

  const advance = (): void => {
    if (step === 0 && !canAdvanceStep1) return;
    if (step === 1 && !canAdvanceStep2) return;
    if (isLast) {
      void submit();
      return;
    }
    setStep((s) => s + 1);
  };

  const footer = (
    <div className="adm-wizard__footer">
      <Button variant="secondary" onClick={back} disabled={submitting}>
        {step === 0 ? 'Cancel' : 'Back'}
      </Button>
      <Button
        onClick={advance}
        disabled={
          (step === 0 && !canAdvanceStep1) || (step === 1 && !canAdvanceStep2) || submitting
        }
        loading={submitting}
        loadingLabel="Creating…"
      >
        {isLast ? 'Create tenant' : 'Continue'}
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={close} title="Create tenant" width="xl" footer={footer} dismissible>
      <div className="adm-wizard__progress">
        <span className="adm-wizard__count">
          Step {step + 1} of {STEPS.length}
        </span>
        <ol className="adm-wizard__steps" aria-label="Wizard steps">
          {STEPS.map((label, i) => (
            <li
              key={label}
              className="adm-wizard__step"
              aria-current={i === step ? 'step' : undefined}
            >
              <span
                className={`adm-wizard__dot ${i <= step ? 'is-on' : ''}`}
                aria-hidden="true"
              />
              <span className={i <= step ? 'adm-wizard__step-label is-active' : 'adm-wizard__step-label'}>
                {label}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div className="adm-wizard__body">
        {step === 0 ? (
          <>
            <div className="adm-field">
              <label className="adm-field__label" htmlFor={nameId}>
                Tenant name
              </label>
              <Input
                id={nameId}
                value={name}
                placeholder="e.g. Borealis Health"
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </div>
            <div className="adm-field">
              {/* A real label, not an aria-label: the mock shows "Plan" above the
                  pills, and without it the row reads as three unexplained buttons. */}
              <span className="adm-field__label" id={planId}>
                Plan
              </span>
              <div className="adm-segmented" role="radiogroup" aria-labelledby={planId}>
                {PLANS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    role="radio"
                    aria-checked={plan === p.value}
                    className={`adm-segmented__opt ${plan === p.value ? 'is-on' : ''}`}
                    onClick={() => {
                      setPlan(p.value);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="adm-note">
              Creating a tenant seeds the base taxonomy (Invoice, PO, Delivery Note…) automatically.
            </p>
          </>
        ) : null}

        {step === 1 ? (
          <>
            <FormField label="Admin name">
              {(p) => (
                <Input
                  {...p}
                  value={adminName}
                  placeholder="Ada Lovelace"
                  onChange={(e) => {
                    setAdminName(e.target.value);
                  }}
                />
              )}
            </FormField>
            <FormField
              label="Admin email"
              error={errorField === 'email' ? (error ?? undefined) : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={email}
                  autoComplete="off"
                  placeholder="admin@tenant.com"
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
              )}
            </FormField>
            <p className="adm-note">
              This is the tenant&rsquo;s first administrator. No password is set here — they&rsquo;ll
              get an email to set their own, then can add more users once they&rsquo;re signed in.
            </p>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <div className="adm-threshold">
              <label className="adm-field__label adm-threshold__label" htmlFor={thresholdId}>
                Confidence threshold
              </label>
              <input
                id={thresholdId}
                type="range"
                min="0.5"
                max="0.95"
                step="0.05"
                value={threshold}
                onChange={(e) => {
                  setThreshold(Number(e.target.value));
                }}
                className="adm-range"
              />
              <span className="adm-threshold__value">{threshold.toFixed(2)}</span>
            </div>
            <div className="adm-field">
              <label className="adm-field__label" htmlFor={hostnameId}>
                Branding hostname
              </label>
              <Input
                id={hostnameId}
                value={hostname}
                placeholder={hostnameFor(trimmedName)}
                onChange={(e) => {
                  setHostname(e.target.value);
                }}
                className="adm-mono"
              />
            </div>
            <p className="adm-note">
              Defaults inherit from your partner branding profile — the tenant can re-skin later.
            </p>
          </>
        ) : null}

        {/* Only what no field owns — a field-bound refusal already shows on it. */}
        {error !== null && errorField === null ? (
          <p className="adm-note adm-note--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
